import { Box, Grid, TextField, Paper } from '@mui/material';
import {
  Formik,
  Form,
  Field,
  ErrorMessage,
  FormikProps,
  FieldInputProps,
  FieldMetaProps,
} from 'formik';
import * as Yup from 'yup';
import { IPhysicalTransship } from 'interfaces/IPhysicalTransship';
import { TpMultiSelectAutocomplete } from 'components/TpMultiSelectAutocomplete';
import { NewButton } from 'components/NewButton';
import CustomCheckBox from 'components/checkbox';

export interface IPhysicalTransshipSubmitValues extends Partial<IPhysicalTransship> {
  transshipPointIds: number[];
}

interface PhysicalTransshipFormProps {
  data: Partial<IPhysicalTransship>;
  isNew: boolean;
  initialAgileDepotIds: number[];
  onSubmit: (values: IPhysicalTransshipSubmitValues) => void | Promise<void>;
}

interface IFormValues {
  name: string;
  initials: string;
  notes: string;
  inUse: boolean;
  transshipPointIds: number[];
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Nome é obrigatório'),
  transshipPointIds: Yup.array()
    .of(Yup.number())
    .min(1, 'Selecione ao menos um CD do Agile vinculado.'),
});

const textFieldInputSx = {
  InputProps: { sx: { height: 56, fontSize: 16 } },
  inputProps: { style: { fontSize: 16 } },
};

export function PhysicalTransshipForm({
  data,
  isNew,
  initialAgileDepotIds,
  onSubmit,
}: PhysicalTransshipFormProps) {
  const initialValues: IFormValues = {
    name: data.name ?? '',
    initials: data.initials ?? '',
    notes: data.notes ?? '',
    inUse: data.inUse !== false,
    transshipPointIds: initialAgileDepotIds,
  };

  const handleSubmit = async (values: IFormValues) => {
    await onSubmit({
      ...data,
      id: data.id,
      name: values.name.trim(),
      initials: values.initials.trim() || null,
      notes: values.notes.trim() || null,
      inUse: values.inUse,
      transshipPointIds: values.transshipPointIds,
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik: FormikProps<IFormValues>) => (
        <Box
          component={Form}
          id="popupForm"
          sx={{ p: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center' }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 0, sm: 2 },
              width: '100%',
              maxWidth: '750px',
              boxSizing: 'border-box',
            }}
          >
            <Grid container spacing={3} width="100%">
              <Grid item xs={12} sm={6}>
                <Field name="name">
                  {({
                    field,
                    meta,
                  }: {
                    field: FieldInputProps<string>;
                    meta: FieldMetaProps<string>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Nome"
                      size="medium"
                      {...textFieldInputSx}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Field name="initials">
                  {({
                    field,
                    meta,
                  }: {
                    field: FieldInputProps<string>;
                    meta: FieldMetaProps<string>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Sigla"
                      size="medium"
                      {...textFieldInputSx}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>

              <Grid item xs={12}>
                <CustomCheckBox
                  text="Ativo"
                  checked={formik.values.inUse}
                  handleToggle={() => {
                    formik.setFieldValue('inUse', !formik.values.inUse);
                    formik.setFieldTouched('inUse', true);
                  }}
                  size="medium"
                />
              </Grid>

              <Grid item xs={12}>
                <TpMultiSelectAutocomplete
                  label="CDs do Agile vinculados *"
                  placeholder="Selecione os CDs do Agile"
                  value={formik.values.transshipPointIds}
                  onChange={(ids) => {
                    formik.setFieldValue('transshipPointIds', ids);
                    formik.setFieldTouched('transshipPointIds', true);
                  }}
                />
                <ErrorMessage name="transshipPointIds">
                  {(msg) => (
                    <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 2 }}>
                      {msg}
                    </Box>
                  )}
                </ErrorMessage>
              </Grid>

              <Grid item xs={12}>
                <Field name="notes">
                  {({
                    field,
                    meta,
                  }: {
                    field: FieldInputProps<string>;
                    meta: FieldMetaProps<string>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Observações"
                      multiline
                      minRows={3}
                      size="medium"
                      inputProps={{ style: { fontSize: 16 } }}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    position: { xs: 'fixed', md: 'static' },
                    left: 0,
                    bottom: 0,
                    width: { xs: '100%', md: 'auto' },
                    bgcolor: 'background.paper',
                    boxShadow: { xs: '0 -2px 8px rgba(0,0,0,0.07)', md: 'none' },
                    zIndex: 1201,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                  }}
                >
                  <NewButton
                    variantType="filled"
                    color="primary"
                    type="submit"
                    disabled={formik.isSubmitting}
                    sx={{ minWidth: ['100%', 120] }}
                  >
                    {formik.isSubmitting
                      ? 'Salvando...'
                      : isNew
                        ? 'Adicionar'
                        : 'Salvar'}
                  </NewButton>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </Formik>
  );
}
